// src/pages/Menu/Finance/FinancialReports.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCcw, Download, TrendingUp, TrendingDown, DollarSign, ArrowLeft, X, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../../context/authContext';
import { Banner } from '../../../components/common/Banner';
import { listIncomes, listIncomeConcepts } from '../../../services/financeIncomesService';
import { listOutcomes, listOutcomeConcepts } from '../../../services/financeOutcomesService';

const Money = (v) => {
  const num = Number(v) || 0;
  // Mostrar sin redondeo, con todos los decimales que tenga
  const parts = num.toString().split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decimalPart = parts[1] || '';
  return `$${integerPart}${decimalPart ? ',' + decimalPart : ''}`;
};

export default function FinancialReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const rut = useMemo(() => localStorage.getItem('rut') || user?.rut || user?.company?.rut || '', [user]);

  const [incomes, setIncomes] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [incomeConcepts, setIncomeConcepts] = useState([]);
  const [outcomeConcepts, setOutcomeConcepts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filterYear, setFilterYear] = useState(() => new Date().getFullYear().toString());
  const [from, setFrom] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  });
  const [filterIncomeConcept, setFilterIncomeConcept] = useState('');
  const [filterOutcomeConcept, setFilterOutcomeConcept] = useState('');
  const [filterType, setFilterType] = useState(''); // 'cuota_social', 'otros', 'todos'
  const [filterCompany, setFilterCompany] = useState('');
  
  // Actualizar fechas cuando cambia el año seleccionado (año completo)
  useEffect(() => {
    if (filterYear) {
      const year = parseInt(filterYear);
      const startDate = new Date(year, 0, 1).toISOString().slice(0, 10); // 1 de enero
      const endDate = new Date(year, 11, 31).toISOString().slice(0, 10); // 31 de diciembre
      setFrom(startDate);
      setTo(endDate);
    }
  }, [filterYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [inc, out, incConcepts, outConcepts] = await Promise.all([
        listIncomes({ rut, from, to }),
        listOutcomes({ rut, from, to }),
        listIncomeConcepts(rut),
        listOutcomeConcepts(rut),
      ]);
      setIncomes(inc || []);
      setOutcomes(out || []);
      setIncomeConcepts(incConcepts || []);
      setOutcomeConcepts(outConcepts || []);
    } catch (e) {
      Swal.fire('Error', e.message || 'No se pudieron cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (rut) loadData(); }, [rut, from, to]);

  // Extraer empresas únicas para el filtro
  const companies = useMemo(() => {
    const companySet = new Set();
    incomes.forEach(inc => {
      if (inc.companyName && inc.companyName !== '-') {
        companySet.add(inc.companyName);
      }
    });
    outcomes.forEach(out => {
      if (out.companyName && out.companyName !== '-') {
        companySet.add(out.companyName);
      }
    });
    return Array.from(companySet).sort();
  }, [incomes, outcomes]);

  // Agrupar ingresos por concepto con detalles
  const incomesByConcept = useMemo(() => {
    const map = {};

    incomes.forEach(inc => {
      // Aplicar filtro por empresa
      if (filterCompany && inc.companyName !== filterCompany) return;

      (inc.lines || []).forEach(line => {
        const conceptName = line.concept?.name || 'Sin concepto';
        const conceptCode = (line.concept?.code || '').toUpperCase();
        const isCuotaSocial = conceptCode === 'CUOTA_SOCIAL' || conceptCode === 'CUOTAS_SOCIALES' || 
                             conceptName.toLowerCase().includes('cuota social');
        
        // Aplicar filtros
        if (filterIncomeConcept && conceptName !== filterIncomeConcept) return;
        if (filterType === 'cuota_social' && !isCuotaSocial) return;
        if (filterType === 'otros' && isCuotaSocial) return;
        
        if (!map[conceptName]) {
          map[conceptName] = {
            concept: conceptName,
            items: [],
            total: 0,
            isCuotaSocial,
          };
        }
        const amount = Number(line.amount) || 0;
        map[conceptName].items.push({
          companyName: inc.companyName || inc.description || 'Sin descripción',
          description: inc.description || inc.companyName || '',
          amount,
          date: inc.date,
        });
        map[conceptName].total += amount;
      });
    });

    return Object.values(map)
      .sort((a, b) => b.total - a.total)
      .map(group => ({
        ...group,
        items: group.items.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)),
      }));
  }, [incomes, filterIncomeConcept, filterType, filterCompany]);

  // Agrupar egresos por concepto con detalles (con sub-items por empresa/descripción)
  const outcomesByConcept = useMemo(() => {
    const map = {};

    outcomes.forEach(out => {
      // Aplicar filtro por empresa
      if (filterCompany && out.companyName !== filterCompany) return;

      (out.lines || []).forEach(line => {
        const conceptName = line.concept?.name || 'Sin concepto';
        
        // Aplicar filtros
        if (filterOutcomeConcept && conceptName !== filterOutcomeConcept) return;
        
        if (!map[conceptName]) {
          map[conceptName] = {
            concept: conceptName,
            items: [],
            total: 0,
          };
        }
        const amount = Number(line.amount) || 0;
        // Agregar item con empresa/descripción como sub-item
        const itemName = out.companyName || out.description || 'Sin descripción';
        map[conceptName].items.push({
          companyName: itemName,
          description: out.description || '',
          amount,
          date: out.date,
        });
        map[conceptName].total += amount;
      });
    });

    return Object.values(map)
      .sort((a, b) => b.total - a.total)
      .map(group => ({
        ...group,
        items: group.items.sort((a, b) => {
          // Ordenar primero por nombre de empresa, luego por fecha
          if (a.companyName !== b.companyName) {
            return a.companyName.localeCompare(b.companyName);
          }
          return new Date(b.date || 0) - new Date(a.date || 0);
        }),
      }));
  }, [outcomes, filterOutcomeConcept, filterCompany]);

  // Calcular totales
  const totalIncomes = useMemo(() => {
    return incomesByConcept.reduce((sum, group) => sum + group.total, 0);
  }, [incomesByConcept]);

  const totalOutcomes = useMemo(() => {
    return outcomesByConcept.reduce((sum, group) => sum + group.total, 0);
  }, [outcomesByConcept]);

  const netResult = totalIncomes - totalOutcomes;

  // Obtener el mes actual del rango de fechas para mostrar
  const currentMonth = useMemo(() => {
    if (!from) return '';
    const date = new Date(from);
    return date.toLocaleString('es-CL', { month: 'long' });
  }, [from]);

  const handleExportPDF = async () => {
    try {
      const { fetchWithAuth } = await import('../../../utils/fetchWithAuth');
      const params = new URLSearchParams({
        rut,
        ...(from && { from }),
        ...(to && { to }),
      });
      
      const response = await fetchWithAuth(`/finance/reports/pdf?${params.toString()}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Error al generar el PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fechaDesde = from ? new Date(from).toISOString().split('T')[0] : 'todo';
      const fechaHasta = to ? new Date(to).toISOString().split('T')[0] : 'todo';
      a.download = `resumen-financiero-${fechaDesde}-${fechaHasta}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      Swal.fire('Éxito', 'PDF generado correctamente', 'success');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      Swal.fire('Error', 'Error al generar el PDF. Por favor, intenta nuevamente.', 'error');
    }
  };

  return (
    <div className="w-full">
      {/* Banner arriba */}
      <div className="relative">
        <Banner 
          title="Reportes Financieros" 
          message="Resumen general de ingresos y egresos por tipo de registro" 
        />
        {/* Botón de volver con animación hover */}
        <button
          onClick={() => navigate('/panel/finanzas')}
          className="absolute top-4 left-4 sm:top-6 sm:left-6 inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 transition-all duration-300 text-sm text-white z-10 group"
          title="Volver"
        >
          <ArrowLeft size={16} className="transition-transform duration-300 group-hover:translate-x-[-4px] group-hover:scale-110" /> 
          <span className="transition-all duration-300 group-hover:font-semibold">Volver</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col gap-3 mb-6">
        {/* Primera fila: Filtros principales */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-gray-600 font-medium">Año</label>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="block rounded border px-3 py-1.5 text-sm min-w-[120px]"
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() - 5 + i;
                  return (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Desde</label>
              <input 
                type="date" 
                value={from} 
                onChange={(e) => setFrom(e.target.value)} 
                className="block rounded border px-3 py-1.5 text-sm" 
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Hasta</label>
              <input 
                type="date" 
                value={to} 
                onChange={(e) => setTo(e.target.value)} 
                className="block rounded border px-3 py-1.5 text-sm" 
              />
            </div>
            <button
              onClick={loadData}
              className="inline-flex items-center gap-2 px-3 py-2 rounded border hover:bg-gray-50"
            >
              <RefreshCcw size={16} /> Refrescar
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleExportPDF}
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98] transition-all duration-200"
            >
              <Download size={16} /> Generar PDF
            </button>
          </div>
        </div>

        {/* Segunda fila: Filtros adicionales */}
        <div className="flex flex-wrap items-end gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <label className="text-xs text-gray-600 font-medium">Empresa</label>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="block rounded border px-3 py-1.5 text-sm min-w-[200px] bg-white"
            >
              <option value="">Todas las empresas</option>
              {companies.map((company) => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium">Tipo de Ingreso</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="block rounded border px-3 py-1.5 text-sm min-w-[160px] bg-white"
            >
              <option value="">Todos los tipos</option>
              <option value="cuota_social">Solo Cuotas Sociales</option>
              <option value="otros">Otros (sin cuotas sociales)</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium">Concepto Ingreso</label>
            <select
              value={filterIncomeConcept}
              onChange={(e) => setFilterIncomeConcept(e.target.value)}
              className="block rounded border px-3 py-1.5 text-sm min-w-[200px] bg-white"
            >
              <option value="">Todos los conceptos</option>
              {incomeConcepts.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium">Concepto Egreso</label>
            <select
              value={filterOutcomeConcept}
              onChange={(e) => setFilterOutcomeConcept(e.target.value)}
              className="block rounded border px-3 py-1.5 text-sm min-w-[200px] bg-white"
            >
              <option value="">Todos los conceptos</option>
              {outcomeConcepts.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {(filterType || filterIncomeConcept || filterOutcomeConcept || filterCompany) && (
            <button
              onClick={() => {
                setFilterType('');
                setFilterIncomeConcept('');
                setFilterOutcomeConcept('');
                setFilterCompany('');
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-red-300 text-red-600 hover:bg-red-50 text-sm"
              title="Limpiar filtros"
            >
              <X size={14} /> Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Resumen de Totales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-green-800">Total Ingresos</h3>
            <TrendingUp className="text-green-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-green-700">{Money(totalIncomes)}</p>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl border-2 border-red-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-red-800">Total Egresos</h3>
            <TrendingDown className="text-red-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-red-700">{Money(totalOutcomes)}</p>
        </div>

        <div className={`bg-gradient-to-br rounded-xl border-2 p-5 shadow-sm ${
          netResult >= 0 
            ? 'from-blue-50 to-blue-100 border-blue-200' 
            : 'from-orange-50 to-orange-100 border-orange-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-sm font-semibold ${netResult >= 0 ? 'text-blue-800' : 'text-orange-800'}`}>
              Resultado Neto
            </h3>
            <DollarSign className={netResult >= 0 ? 'text-blue-600' : 'text-orange-600'} size={20} />
          </div>
          <p className={`text-2xl font-bold ${netResult >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
            {Money(netResult)}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-600">Cargando reportes...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Columna de Ingresos */}
          <div className="bg-white rounded-xl border-2 border-gray-300 overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-green-100 to-green-50 border-b-2 border-green-300 px-4 py-3">
              <h3 className="font-bold text-green-800 text-lg">INGRESOS</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 border-b-2 border-gray-300 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Tipo de registro</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Totales</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Fila del mes */}
                  {currentMonth && (
                    <tr className="bg-green-50/30 border-b border-green-200">
                      <td className="px-3 py-2 font-semibold text-green-800 capitalize">{currentMonth}</td>
                      <td className="px-3 py-2 text-right font-semibold text-green-700">{Money(totalIncomes)}</td>
                    </tr>
                  )}
                  {incomesByConcept.map((group, idx) => (
                    <React.Fragment key={idx}>
                      {/* Fila del concepto */}
                      <tr className="bg-green-50/50 border-b border-green-200">
                        <td className="px-3 py-2 font-bold text-green-800">{group.concept.toUpperCase()}</td>
                        <td className="px-3 py-2 text-right font-bold text-green-700">{Money(group.total)}</td>
                      </tr>
                      {/* Filas de items (sub-items indentados) */}
                      {group.items.map((item, itemIdx) => (
                        <tr key={`${idx}-${itemIdx}`} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-1.5 pl-6">
                            <div className="text-xs text-gray-700">
                              <div className="font-medium">{item.companyName}</div>
                              {item.description && item.description !== item.companyName && (
                                <div className="text-gray-500 italic text-xs mt-0.5">{item.description}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-right text-green-600 font-semibold text-xs">
                            {Money(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {incomesByConcept.length === 0 && (
                    <tr>
                      <td colSpan="2" className="px-3 py-8 text-center text-gray-400 italic text-xs">
                        No hay ingresos en el período seleccionado
                      </td>
                    </tr>
                  )}
                  {/* Fila de total */}
                  <tr className="bg-gray-200 border-t-2 border-gray-400 font-bold">
                    <td className="px-3 py-2.5">Suma Ingresos</td>
                    <td className="px-3 py-2.5 text-right text-green-700">{Money(totalIncomes)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Columna de Egresos */}
          <div className="bg-white rounded-xl border-2 border-gray-300 overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-red-100 to-red-50 border-b-2 border-red-300 px-4 py-3">
              <h3 className="font-bold text-red-800 text-lg">EGRESOS</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 border-b-2 border-gray-300 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Tipo de registro</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Totales</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Fila del mes */}
                  {currentMonth && (
                    <tr className="bg-red-50/30 border-b border-red-200">
                      <td className="px-3 py-2 font-semibold text-red-800 capitalize">{currentMonth}</td>
                      <td className="px-3 py-2 text-right font-semibold text-red-700">{Money(totalOutcomes)}</td>
                    </tr>
                  )}
                  {outcomesByConcept.map((group, idx) => (
                    <React.Fragment key={idx}>
                      {/* Fila del concepto principal */}
                      <tr className="bg-red-50/50 border-b border-red-200">
                        <td className="px-3 py-2 font-bold text-red-800">{group.concept.toUpperCase()}</td>
                        <td className="px-3 py-2 text-right font-bold text-red-700">{Money(group.total)}</td>
                      </tr>
                      {/* Filas de items (sub-items indentados) */}
                      {group.items.map((item, itemIdx) => (
                        <tr key={`${idx}-${itemIdx}`} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-1.5 pl-6">
                            <div className="text-xs text-gray-700">
                              <div className="font-medium">{item.companyName}</div>
                              {item.description && item.description !== item.companyName && (
                                <div className="text-gray-500 italic text-xs mt-0.5">{item.description}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-right text-red-600 font-semibold text-xs">
                            {Money(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {outcomesByConcept.length === 0 && (
                    <tr>
                      <td colSpan="2" className="px-3 py-8 text-center text-gray-400 italic text-xs">
                        No hay egresos en el período seleccionado
                      </td>
                    </tr>
                  )}
                  {/* Fila de total */}
                  <tr className="bg-gray-200 border-t-2 border-gray-400 font-bold">
                    <td className="px-3 py-2.5">Suma Egresos</td>
                    <td className="px-3 py-2.5 text-right text-red-700">{Money(totalOutcomes)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Columna de Resumen Final */}
          <div className="bg-white rounded-xl border-2 border-gray-300 overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-blue-100 to-blue-50 border-b-2 border-blue-300 px-4 py-3">
              <h3 className="font-bold text-blue-800 text-lg">RESUMEN FINAL</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 border-b-2 border-gray-300 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Periodo</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Totales</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Resumen Final */}
                  <tr className="bg-blue-50/30 border-t-2 border-blue-200">
                    <td className="px-3 py-2 font-bold text-blue-800" colSpan="2">Resumen Final</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-3 py-1.5 text-gray-700">SUMA INGRESOS</td>
                    <td className="px-3 py-1.5 text-right font-semibold text-green-700">{Money(totalIncomes)}</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-3 py-1.5 text-gray-700">SUMA SALDO ANTERIOR</td>
                    <td className="px-3 py-1.5 text-right text-gray-600">$0</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-3 py-1.5 text-gray-700">SUMA EGRESOS</td>
                    <td className="px-3 py-1.5 text-right font-semibold text-red-700">-{Money(totalOutcomes)}</td>
                  </tr>
                  <tr className="bg-gray-200 border-t-2 border-gray-400 font-bold">
                    <td className="px-3 py-2.5 text-gray-800">RESULTADO NETO</td>
                    <td className={`px-3 py-2.5 text-right font-bold ${netResult >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {Money(netResult)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
